import jsPDF from 'jspdf';
import { format, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface MetricsData {
  mrr: number;
  mrrChange: number;
  arr: number;
  arrChange: number;
  churn: number;
  churnChange: number;
  totalRevenue: number;
  revenueChange: number;
  cac: number;
  ltv: number;
  averageTicket: number;
  totalCustomers: number;
}

interface TimeSeriesData {
  month: string;
  revenue?: number;
  churn?: number;
  target?: number;
}

interface ProductReportData {
  product: {
    id: string;
    name: string;
    logo_url: string | null;
  };
  metrics: MetricsData;
  timeSeries: {
    revenueData: TimeSeriesData[];
    churnData: TimeSeriesData[];
  };
}

interface ReportData {
  metrics: MetricsData;
  revenueTimeSeries?: TimeSeriesData[];
  churnTimeSeries?: TimeSeriesData[];
  startDate: Date;
  endDate: Date;
  productName?: string;
}

interface ReportOptions {
  reportType: 'monthly' | 'quarterly' | 'custom';
  userName: string;
  companyName: string;
  companyLogo?: string;
}

const drawReportHeader = async (
  pdf: jsPDF,
  companyName: string,
  companyLogo?: string
) => {
  const margin = 15;
  const pageWidth = 210;
  
  // Draw company logo if available
  if (companyLogo) {
    try {
      pdf.addImage(companyLogo, 'PNG', margin, margin, 15, 15);
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text(companyName, margin + 20, margin + 10);
    } catch (error) {
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text(companyName, margin, margin + 10);
    }
  } else {
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.text(companyName, margin, margin + 10);
  }
  
  // Draw horizontal line
  pdf.setDrawColor(226, 232, 240);
  pdf.setLineWidth(0.5);
  pdf.line(margin, margin + 18, pageWidth - margin, margin + 18);
};

const drawReportFooter = (pdf: jsPDF, userName: string) => {
  const margin = 15;
  const pageWidth = 210;
  const pageHeight = 297;
  const footerY = pageHeight - margin - 10;
  
  // Draw horizontal line
  pdf.setDrawColor(226, 232, 240);
  pdf.setLineWidth(0.5);
  pdf.line(margin, footerY - 3, pageWidth - margin, footerY - 3);
  
  // Draw footer text
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(100, 100, 100);
  
  const now = new Date();
  const dateStr = format(now, 'dd/MM/yyyy');
  const timeStr = format(now, 'HH:mm');
  const footerText = `Relatório gerado em ${dateStr} às ${timeStr} por ${userName} no Dashi.app.br`;
  
  pdf.text(footerText, margin, footerY + 3);
};

const formatCurrency = (value: number) => {
  return `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatPercentage = (value: number) => {
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}%`;
};

const addReportTitle = (
  pdf: jsPDF,
  reportType: string,
  startDate: Date,
  endDate: Date,
  productName?: string
) => {
  const margin = 15;
  const pageWidth = 210;
  let yPos = 45;
  
  // Report type title
  pdf.setFontSize(18);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(0, 0, 0);
  
  const titleMap: Record<string, string> = {
    monthly: 'RELATÓRIO MENSAL',
    quarterly: 'RELATÓRIO TRIMESTRAL',
    custom: 'RELATÓRIO PERSONALIZADO',
  };
  
  const title = titleMap[reportType] || 'RELATÓRIO';
  const titleWidth = pdf.getTextWidth(title);
  pdf.text(title, (pageWidth - titleWidth) / 2, yPos);
  
  yPos += 10;
  
  // Period
  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(100, 100, 100);
  const periodText = `Período: ${format(startDate, 'dd/MM/yyyy', { locale: ptBR })} - ${format(endDate, 'dd/MM/yyyy', { locale: ptBR })}`;
  const periodWidth = pdf.getTextWidth(periodText);
  pdf.text(periodText, (pageWidth - periodWidth) / 2, yPos);
  
  yPos += 8;
  
  // Product name if provided
  if (productName) {
    const productText = `Produto: ${productName}`;
    const productWidth = pdf.getTextWidth(productText);
    pdf.text(productText, (pageWidth - productWidth) / 2, yPos);
    yPos += 8;
  }
  
  return yPos + 5;
};

const addMetricsCards = (pdf: jsPDF, metrics: MetricsData, yPos: number) => {
  const margin = 15;
  const pageWidth = 210;
  const cardWidth = 43;
  const cardHeight = 28;
  const gap = 3;
  const startX = margin;
  
  const metricsData = [
    { label: 'MRR', value: formatCurrency(metrics.mrr), change: formatPercentage(metrics.mrrChange) },
    { label: 'ARR', value: formatCurrency(metrics.arr), change: formatPercentage(metrics.arrChange) },
    { label: 'Churn', value: `${metrics.churn.toFixed(1)}%`, change: formatPercentage(metrics.churnChange) },
    { label: 'Receita Total', value: formatCurrency(metrics.totalRevenue), change: formatPercentage(metrics.revenueChange) },
    { label: 'CAC', value: formatCurrency(metrics.cac), change: null },
    { label: 'LTV', value: formatCurrency(metrics.ltv), change: null },
    { label: 'Ticket Médio', value: formatCurrency(metrics.averageTicket), change: null },
    { label: 'Clientes', value: metrics.totalCustomers.toString(), change: null },
  ];
  
  let currentY = yPos;
  
  // Draw first row (4 cards)
  for (let i = 0; i < 4; i++) {
    const x = startX + (i * (cardWidth + gap));
    const metric = metricsData[i];
    
    // Card background
    pdf.setFillColor(249, 250, 251);
    pdf.setDrawColor(226, 232, 240);
    pdf.setLineWidth(0.3);
    pdf.roundedRect(x, currentY, cardWidth, cardHeight, 2, 2, 'FD');
    
    // Label
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(100, 100, 100);
    pdf.text(metric.label, x + 3, currentY + 6);
    
    // Value
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(0, 0, 0);
    pdf.text(metric.value, x + 3, currentY + 15);
    
    // Change
    if (metric.change) {
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      const isPositive = parseFloat(metric.change) > 0;
      if (isPositive) {
        pdf.setTextColor(34, 197, 94);
      } else {
        pdf.setTextColor(239, 68, 68);
      }
      pdf.text(metric.change, x + 3, currentY + 23);
    }
  }
  
  currentY += cardHeight + gap + 3;
  
  // Draw second row (4 cards)
  for (let i = 4; i < 8; i++) {
    const x = startX + ((i - 4) * (cardWidth + gap));
    const metric = metricsData[i];
    
    // Card background
    pdf.setFillColor(249, 250, 251);
    pdf.setDrawColor(226, 232, 240);
    pdf.setLineWidth(0.3);
    pdf.roundedRect(x, currentY, cardWidth, cardHeight, 2, 2, 'FD');
    
    // Label
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(100, 100, 100);
    pdf.text(metric.label, x + 3, currentY + 6);
    
    // Value
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(0, 0, 0);
    pdf.text(metric.value, x + 3, currentY + 15);
    
    // Change
    if (metric.change) {
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      const isPositive = parseFloat(metric.change) > 0;
      if (isPositive) {
        pdf.setTextColor(34, 197, 94);
      } else {
        pdf.setTextColor(239, 68, 68);
      }
      pdf.text(metric.change, x + 3, currentY + 23);
    }
  }
  
  return currentY + cardHeight + 10;
};

const addTimeSeriesTable = (
  pdf: jsPDF,
  title: string,
  data: TimeSeriesData[],
  type: 'revenue' | 'churn',
  yPos: number
) => {
  const margin = 15;
  const pageWidth = 210;
  const tableWidth = pageWidth - (margin * 2);
  
  // Title
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(0, 0, 0);
  pdf.text(title, margin, yPos);
  
  yPos += 8;
  
  // Table header
  const colWidths = [60, 60, 60];
  const headers = ['Mês', type === 'revenue' ? 'Receita' : 'Churn (%)', 'Meta'];
  
  pdf.setFillColor(241, 245, 249);
  pdf.rect(margin, yPos, tableWidth, 8, 'F');
  
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(0, 0, 0);
  
  let xPos = margin + 3;
  headers.forEach((header, i) => {
    pdf.text(header, xPos, yPos + 5);
    xPos += colWidths[i];
  });
  
  yPos += 8;
  
  // Table rows
  pdf.setFont('helvetica', 'normal');
  data.slice(0, 10).forEach((item, index) => {
    const bgColor = index % 2 === 0 ? 255 : 249;
    pdf.setFillColor(bgColor, bgColor, bgColor);
    pdf.rect(margin, yPos, tableWidth, 7, 'F');
    
    pdf.setTextColor(0, 0, 0);
    
    xPos = margin + 3;
    pdf.text(item.month, xPos, yPos + 5);
    xPos += colWidths[0];
    
    const value = type === 'revenue' 
      ? (item.revenue ? formatCurrency(item.revenue) : '-')
      : (item.churn ? `${item.churn.toFixed(1)}%` : '-');
    pdf.text(value, xPos, yPos + 5);
    xPos += colWidths[1];
    
    const target = item.target 
      ? (type === 'revenue' ? formatCurrency(item.target) : `${item.target.toFixed(1)}%`)
      : '-';
    pdf.text(target, xPos, yPos + 5);
    
    yPos += 7;
  });
  
  return yPos + 5;
};

export const generateReport = async (
  data: ReportData,
  options: ReportOptions,
  productDataList?: ProductReportData[]
): Promise<void> => {
  try {
    const pdf = new jsPDF('p', 'mm', 'a4');
    
    // ========== PÁGINA INICIAL ==========
    await drawReportHeader(pdf, options.companyName, options.companyLogo);
    drawReportFooter(pdf, options.userName);
    
    // Add report title (Consolidado se houver produtos, senão nome do produto)
    const titleProductName = data.productName || (productDataList && productDataList.length > 0 ? 'CONSOLIDADO (Todos os Produtos)' : undefined);
    let yPos = addReportTitle(
      pdf,
      options.reportType,
      data.startDate,
      data.endDate,
      titleProductName
    );
    
    // Add metrics cards
    yPos = addMetricsCards(pdf, data.metrics, yPos);
    
    // Add revenue time series if available
    if (data.revenueTimeSeries && data.revenueTimeSeries.length > 0) {
      yPos = addTimeSeriesTable(
        pdf,
        'EVOLUÇÃO DE RECEITA',
        data.revenueTimeSeries,
        'revenue',
        yPos
      );
    }
    
    // Add churn time series if available
    if (data.churnTimeSeries && data.churnTimeSeries.length > 0) {
      if (yPos > 220) {
        pdf.addPage();
        await drawReportHeader(pdf, options.companyName, options.companyLogo);
        drawReportFooter(pdf, options.userName);
        yPos = 45;
      }
      
      yPos = addTimeSeriesTable(
        pdf,
        'EVOLUÇÃO DE CHURN',
        data.churnTimeSeries,
        'churn',
        yPos
      );
    }
    
    // ========== PÁGINAS POR PRODUTO ==========
    if (productDataList && productDataList.length > 0) {
      for (const productData of productDataList) {
        pdf.addPage();
        await drawReportHeader(pdf, options.companyName, options.companyLogo);
        drawReportFooter(pdf, options.userName);
        
        let productYPos = addReportTitle(
          pdf,
          options.reportType,
          data.startDate,
          data.endDate,
          productData.product.name
        );
        
        productYPos = addMetricsCards(pdf, productData.metrics, productYPos);
        
        if (productData.timeSeries.revenueData.length > 0) {
          productYPos = addTimeSeriesTable(
            pdf,
            'EVOLUÇÃO DE RECEITA',
            productData.timeSeries.revenueData,
            'revenue',
            productYPos
          );
        }
        
        if (productData.timeSeries.churnData.length > 0) {
          if (productYPos > 220) {
            pdf.addPage();
            await drawReportHeader(pdf, options.companyName, options.companyLogo);
            drawReportFooter(pdf, options.userName);
            productYPos = 45;
          }
          
          productYPos = addTimeSeriesTable(
            pdf,
            'EVOLUÇÃO DE CHURN',
            productData.timeSeries.churnData,
            'churn',
            productYPos
          );
        }
      }
    }
    
    // Generate filename
    const typeMap: Record<string, string> = {
      monthly: 'mensal',
      quarterly: 'trimestral',
      custom: 'personalizado',
    };
    
    const reportTypeName = typeMap[options.reportType] || 'relatorio';
    const dateStr = format(data.endDate, 'MMM-yyyy', { locale: ptBR });
    const fileName = `dashi-relatorio-${reportTypeName}-${dateStr}.pdf`;
    
    // Save PDF
    pdf.save(fileName);
  } catch (error) {
    console.error('Error generating PDF report:', error);
    throw new Error('Falha ao gerar relatório PDF');
  }
};

export const generateMonthlyReport = async (
  metricsData: MetricsData,
  revenueTimeSeries: TimeSeriesData[],
  churnTimeSeries: TimeSeriesData[],
  userName: string,
  companyName: string,
  companyLogo?: string,
  productName?: string,
  productDataList?: ProductReportData[]
) => {
  const now = new Date();
  const startDate = startOfMonth(subMonths(now, 1));
  const endDate = endOfMonth(subMonths(now, 1));
  
  await generateReport(
    {
      metrics: metricsData,
      revenueTimeSeries,
      churnTimeSeries,
      startDate,
      endDate,
      productName,
    },
    {
      reportType: 'monthly',
      userName,
      companyName,
      companyLogo,
    },
    productDataList
  );
};

export const generateQuarterlyReport = async (
  metricsData: MetricsData,
  revenueTimeSeries: TimeSeriesData[],
  churnTimeSeries: TimeSeriesData[],
  userName: string,
  companyName: string,
  companyLogo?: string,
  productName?: string,
  productDataList?: ProductReportData[]
) => {
  const now = new Date();
  const endDate = endOfMonth(subMonths(now, 1));
  const startDate = startOfMonth(subMonths(now, 3));
  
  await generateReport(
    {
      metrics: metricsData,
      revenueTimeSeries,
      churnTimeSeries,
      startDate,
      endDate,
      productName,
    },
    {
      reportType: 'quarterly',
      userName,
      companyName,
      companyLogo,
    },
    productDataList
  );
};

export const generateCustomReport = async (
  metricsData: MetricsData,
  revenueTimeSeries: TimeSeriesData[],
  churnTimeSeries: TimeSeriesData[],
  startDate: Date,
  endDate: Date,
  userName: string,
  companyName: string,
  companyLogo?: string,
  productName?: string,
  productDataList?: ProductReportData[]
) => {
  await generateReport(
    {
      metrics: metricsData,
      revenueTimeSeries,
      churnTimeSeries,
      startDate,
      endDate,
      productName,
    },
    {
      reportType: 'custom',
      userName,
      companyName,
      companyLogo,
    },
    productDataList
  );
};
